import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { CodeAddModify } from '../../../../i18n'
import { getCodeByCodeNameURL } from '../../../NonReact/constants/ApiConstants'
// Utilities imports.
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { getCMEIDFromTelSys, resolvePartitionInTelSys } from '../../Common/API/ItemGetters'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { requestGetJSON } from '../../../NonReact/common/API/REST/RESTAPIClient'
// Commons imports.
import { Modal } from '../../../ReactRouting/Components/Molecules/Modal/Modal'
import { DocTopic } from '../../Common/Constants/Documentation'
// Contexts imports.
import { ModalLoading } from '../../Common/Components/ModalLoading'
import {
    LanguageContextProvider,
    MessageContextProvider,
    PromptContextProvider,
    ReactState,
    UserInfoContextProvider
} from '../../Common/Contexts'
import { CommonFormContextProvider } from '../../Common/Contexts/CommonFormContext'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { clearModalHelpTopic, setModalHelpTopicID } from '../../Common/Utils/HelpPortal'
import AddCodeTabs from '../AddCodesTabs'
import { CodeTypesStringValues } from '../Constants/Constants'
import { ApplicationCodes, ApplicationCodesResponse } from '../Types/Types'
import { JSONResponse } from '../../../NonReact/common/Types/Types'
import { $window } from '../../../NonReact/common/AkixiWindow'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'

type Props = {
    refreshAction: () => void
    filterCMEID?: string
    filterTelSysID?: number
    /** Partition GUID accepted for callers that only have the GUID form available. */
    filterPartitionID?: string
    /** Partition PartIDInTelSys accepted for callers that already have the numeric partition id. */
    filterPartIDInTelSys?: string | number
    filterCodeType?: string
    /**
     * When set (e.g. from code list row), used as the final path segment for CODEINFO / get-by-name URL.
     * Must be {@link CodeTypesStringValues.ACCOUNT_CODE} or {@link CodeTypesStringValues.AGENT_NA} to match the admin API.
     */
    codeTypePathSegment?: string
    selectedCode?: string
    viewOnly?: boolean
    /** Invoked after the dialog has fully closed (e.g. to unmount the host and clear open state). */
    onModalExited?: () => void
}

type AddCodeTabsContainerProps = {
    viewOnly?: boolean
    closeModal: () => void
    filterCMEID?: string
    filterTelSysID?: string | number
    filterPartitionID?: string
    filterPartIDInTelSys?: string | number
    filterCodeType?: string
    codeTypePathSegment?: string
    selectedCode?: string
    refreshAction: () => void
    language: CLanguageHandler
}

// Required for userInfo context loading.
const AddCodesTabsContainer: FC<AddCodeTabsContainerProps> = (props: AddCodeTabsContainerProps): ReactElement => {
    const { displayError }: MessageContextType = useContext(MessageContext)
    const [partitionID, setPartitionID]: ReactState<Nullable<number>> = useState<Nullable<number>>()
    const [cmeID, setCmeID]: ReactState<Nullable<string>> = useState<Nullable<string>>()

    // selected code info
    const [currentCode, setCurrentCode]: ReactState<Nullable<ApplicationCodes>> =
        useState<Nullable<ApplicationCodes>>(null)

    const isMountedRef: MutableRefObject<boolean> = useIsMountedRef()

    const {
        filterCMEID,
        filterTelSysID,
        filterPartitionID,
        filterPartIDInTelSys,
        filterCodeType,
        codeTypePathSegment,
        selectedCode,
        closeModal,
        language
    }: AddCodeTabsContainerProps = props

    const mapCodeTypeToValue = <T extends string>(codeType: Nullable<T>): string => {
        switch (codeType) {
            case language.get(CodeAddModify.CodeAddModify_AccountAuthorisation):
                return CodeTypesStringValues.ACCOUNT_CODE
            case language.get(CodeAddModify.CodeAddModify_ACDNotAvailable):
                return CodeTypesStringValues.AGENT_NA
            default:
                return CodeTypesStringValues.ACCOUNT_CODE
        }
    }

    /** URL path segment for get-by-name; row-driven segment avoids mismatch with list API ("Account Code" / "Agent NA"). */
    const resolvedUrlCodeTypeSegment: string = useMemo((): string => {
        if (!IsStringEmpty(codeTypePathSegment)) {
            return codeTypePathSegment
        }
        return mapCodeTypeToValue(filterCodeType) ?? EMPTY_STRING
    }, [codeTypePathSegment, filterCodeType, language])

    /** Form filter label aligned with {@link resolvedUrlCodeTypeSegment} when the row supplied a path segment. */
    const filterCodeTypeForForm: Definable<string> = useMemo((): Definable<string> => {
        if (!IsStringEmpty(codeTypePathSegment)) {
            return codeTypePathSegment === CodeTypesStringValues.AGENT_NA
                ? language.get(CodeAddModify.CodeAddModify_ACDNotAvailable)
                : language.get(CodeAddModify.CodeAddModify_AccountAuthorisation)
        }

        return filterCodeType
    }, [codeTypePathSegment, filterCodeType, language])

    // Fetch selected code by name once tel sys + partition are known. Do not depend on cmeID: it resolves
    // separately from partitionID and would re-run this effect (duplicate CODEINFO) when cmeID updates.
    useEffect((): void => {
        if (!selectedCode || IsVarEmpty(filterTelSysID) || IsVarEmpty(partitionID)) {
            return
        }

        requestGetJSON<ApplicationCodesResponse>(
            getCodeByCodeNameURL(`${filterTelSysID}`, `${partitionID}`, selectedCode, resolvedUrlCodeTypeSegment)
        )
            .then((response: JSONResponse<ApplicationCodesResponse>): void => {
                if (!isMountedRef.current) return
                const data: Definable<ApplicationCodes> = response?.Data

                if (!IsVarEmpty(data)) {
                    setCurrentCode(data)
                }
            })
            .catch((e: Error): void => {
                if (!isMountedRef.current) return
                displayError(e)
                closeModal()
            })
    }, [selectedCode, filterTelSysID, partitionID, resolvedUrlCodeTypeSegment, closeModal, displayError])

    useEffect((): void => {
        if (!IsVarEmpty(filterTelSysID) && filterTelSysID !== ALL_VALUE) {
            getCMEIDFromTelSys(filterTelSysID, filterCMEID)
                .then((resolvedCmeID: Nullable<string>): void => {
                    if (isMountedRef.current) {
                        setCmeID(resolvedCmeID)
                    }
                })
                .catch((e: Error): void => {
                    if (isMountedRef.current) {
                        displayError(e)
                        setCmeID(EMPTY_STRING)
                    }
                })
            resolvePartitionInTelSys(filterPartIDInTelSys, filterPartitionID)
                .then((id: Nullable<number>): void => {
                    if (isMountedRef.current) {
                        setPartitionID(id)
                    }
                })
                .catch((e: Error): void => {
                    if (isMountedRef.current) {
                        displayError(e)
                        setPartitionID(ALL_VALUE)
                    }
                })
        } else {
            setCmeID(EMPTY_STRING)
            setPartitionID(ALL_VALUE)
        }
    }, [filterCMEID, filterPartIDInTelSys, filterPartitionID, filterTelSysID, displayError])

    if (!IsStringEmpty(selectedCode) && currentCode === null) {
        return <ModalLoading tabs={2} sections={5} />
    }

    return isVarNotEmpty(partitionID) && isVarNotEmpty(cmeID) ? (
        <AddCodeTabs
            {...props}
            selectedCode={selectedCode === null ? undefined : currentCode}
            filters={{
                cmeID: cmeID ?? EMPTY_STRING,
                telSysID: parseInt(`${filterTelSysID}`, 10),
                partitionID: partitionID ?? undefined,
                codeType: filterCodeTypeForForm
            }}
        />
    ) : (
        <ModalLoading tabs={2} sections={5} />
    )
}

const AddUserModal: FC<Props> = ({
    filterCMEID,
    filterTelSysID,
    filterPartitionID,
    filterPartIDInTelSys,
    filterCodeType,
    codeTypePathSegment,
    selectedCode,
    refreshAction,
    viewOnly,
    onModalExited
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(CodeAddModify.NAME)

    // Whether the modal window is open.
    const [isModalOpen, setModalOpen] = useState<boolean>(true)

    const closeModal = (): void => {
        $window.disableHotKeys = false
        // Component is controlled externally.
        setModalOpen(false)
    }

    return (
        <div>
            <CommonFormContextProvider>
                <Modal
                    isOpen={isModalOpen}
                    closeOnBackdropClick={true}
                    isClosableWithXButton={true}
                    inlineExitPromptSettings={{
                        message: language.get(CodeAddModify.CodeAddModify_PromptMessage),
                        confirmButtonText: language.get(CodeAddModify.CodeAddModify_PromptConfirm),
                        cancelButtonText: language.get(CodeAddModify.CodeAddModify_PromptCancel)
                    }}
                    onOpen={(): void => {
                        // Add contextual doc topic ID to the window object which is used
                        // in old JSP logic that handles F1 key press.
                        setModalHelpTopicID(DocTopic.ADMINCODESADD)
                    }}
                    onClose={closeModal}
                    onExited={(): void => {
                        clearModalHelpTopic()
                        onModalExited?.()
                    }}
                >
                    {isModalOpen ? (
                        <LanguageContextProvider>
                            <PromptContextProvider>
                                <MessageContextProvider>
                                    <UserInfoContextProvider>
                                        <div className='modal-title-report-config'>
                                            {selectedCode
                                                ? language.get(CodeAddModify.ModifyCodeTitle)
                                                : language.get(CodeAddModify.AddUserTitle)}
                                        </div>
                                        <div className='code-add-modify'>
                                            <AddCodesTabsContainer
                                                filterCMEID={filterCMEID}
                                                filterTelSysID={filterTelSysID}
                                                filterPartitionID={filterPartitionID}
                                                filterPartIDInTelSys={filterPartIDInTelSys}
                                                filterCodeType={filterCodeType}
                                                codeTypePathSegment={codeTypePathSegment}
                                                selectedCode={selectedCode}
                                                viewOnly={viewOnly}
                                                closeModal={closeModal}
                                                refreshAction={refreshAction}
                                                language={language}
                                            />
                                        </div>
                                    </UserInfoContextProvider>
                                </MessageContextProvider>
                            </PromptContextProvider>
                        </LanguageContextProvider>
                    ) : (
                        <> </>
                    )}
                </Modal>
            </CommonFormContextProvider>
        </div>
    )
}

export default AddUserModal
